"use client";

import {
  ShowWithComedian,
  updateTixologiAvailabilityClient,
} from "@punchup/data/legacy";
import dayjs from "@punchup/utils/dayjs";
import { getApiClient } from "@punchup/utils";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQueries, useQuery, useQueryClient } from "@tanstack/react-query";

/**
 * Number of consecutive empty month fetches before we stop allowing forward navigation
 */
const MAX_EMPTY_MONTHS = 3;

/**
 * Fetch shows for a single month from the API
 */
const fetchMonthShows = async (
  venuePageId: string,
  month: dayjs.Dayjs
): Promise<ShowWithComedian[]> => {
  const startDatetime = month.startOf("month").toISOString();
  const endDatetime = month.add(1, "month").startOf("month").toISOString();

  const rawShows = await getApiClient().get<ShowWithComedian[]>(
    `/api/shows?venuePageId=${venuePageId}&startDatetime=${startDatetime}&endDatetime=${endDatetime}`
  );
  const shows = await updateTixologiAvailabilityClient(rawShows);

  return [...shows].sort(
    (a, b) => dayjs(a.datetime).valueOf() - dayjs(b.datetime).valueOf()
  );
};

interface UseVenueCalendarShowsOptions {
  venuePageId: string;
  initialShows: ShowWithComedian[];
}

interface MonthData {
  month: dayjs.Dayjs;
  shows: ShowWithComedian[];
}

interface UseVenueCalendarShowsResult {
  currentMonth: dayjs.Dayjs;
  currentMonthShows: ShowWithComedian[];
  goToNextMonth: () => void;
  goToPrevMonth: () => void;
  canGoNext: boolean;
  canGoPrev: boolean;
  isLoading: boolean;
  /** All months loaded so far (for mobile infinite scroll) */
  allMonths: MonthData[];
  /** Load the next month beyond the furthest loaded (for mobile infinite scroll) */
  loadNextMonth: () => void;
  /** Whether more months may exist beyond what's loaded */
  hasMore: boolean;
  /** Whether a new month is currently being fetched via loadNextMonth */
  isLoadingMore: boolean;
}

export function useVenueCalendarShows({
  venuePageId,
  initialShows,
}: UseVenueCalendarShowsOptions): UseVenueCalendarShowsResult {
  const queryClient = useQueryClient();
  const [currentMonth, setCurrentMonth] = useState(() =>
    dayjs().startOf("month")
  );
  const [noMoreForward, setNoMoreForward] = useState(false);
  const consecutiveEmptyRef = useRef(0);
  const seededRef = useRef(false);

  // Seed cache with initial server-rendered shows on first render
  if (!seededRef.current && initialShows.length > 0) {
    seededRef.current = true;
    const showsByMonth = new Map<string, ShowWithComedian[]>();

    for (const show of initialShows) {
      const monthKey = dayjs(show.datetime).startOf("month").format("YYYY-MM");
      const existing = showsByMonth.get(monthKey) ?? [];
      existing.push(show);
      showsByMonth.set(monthKey, existing);
    }

    for (const [monthKey, shows] of showsByMonth) {
      const sorted = [...shows].sort(
        (a, b) => dayjs(a.datetime).valueOf() - dayjs(b.datetime).valueOf()
      );
      queryClient.setQueryData(
        ["venueCalendarShows", venuePageId, monthKey],
        sorted
      );
    }
  }

  const monthKey = currentMonth.format("YYYY-MM");

  // Query for current month
  const { data: currentMonthShows = [], isLoading } = useQuery({
    queryKey: ["venueCalendarShows", venuePageId, monthKey],
    queryFn: () => fetchMonthShows(venuePageId, currentMonth),
    staleTime: 5 * 60 * 1000,
  });

  // Track consecutive empty months for no-more detection
  const lastCheckedMonthRef = useRef<string>("");
  if (
    monthKey !== lastCheckedMonthRef.current &&
    !isLoading &&
    currentMonthShows !== undefined
  ) {
    lastCheckedMonthRef.current = monthKey;
    if (currentMonthShows.length === 0) {
      consecutiveEmptyRef.current += 1;
      if (consecutiveEmptyRef.current >= MAX_EMPTY_MONTHS) {
        setNoMoreForward(true);
      }
    } else {
      consecutiveEmptyRef.current = 0;
      if (noMoreForward) {
        setNoMoreForward(false);
      }
    }
  }

  // Prefetch ahead when month changes
  const prefetchMonth = useCallback(
    (month: dayjs.Dayjs) => {
      const key = month.format("YYYY-MM");
      queryClient.prefetchQuery({
        queryKey: ["venueCalendarShows", venuePageId, key],
        queryFn: () => fetchMonthShows(venuePageId, month),
        staleTime: 5 * 60 * 1000,
      });
    },
    [queryClient, venuePageId]
  );

  const today = useMemo(() => dayjs().startOf("month"), []);
  const canGoPrev = currentMonth.isAfter(today, "month");
  const canGoNext = !noMoreForward;

  const goToNextMonth = useCallback(() => {
    if (!canGoNext) return;
    setCurrentMonth((prev) => {
      const next = prev.add(1, "month");
      // Prefetch M+1 and M+2 from the new month
      prefetchMonth(next.add(1, "month"));
      prefetchMonth(next.add(2, "month"));
      return next;
    });
  }, [canGoNext, prefetchMonth]);

  const goToPrevMonth = useCallback(() => {
    if (!canGoPrev) return;
    setCurrentMonth((prev) => prev.subtract(1, "month"));
    // Reset no-more detection when going backwards
    setNoMoreForward(false);
    consecutiveEmptyRef.current = 0;
  }, [canGoPrev]);

  // --- Mobile infinite scroll support ---

  // Track the furthest month we've loaded for infinite scroll
  const [furthestMonth, setFurthestMonth] = useState(() => {
    if (initialShows.length === 0) return dayjs().startOf("month");
    // Find the latest month from initial shows
    const latestShow = initialShows.reduce((latest, show) => {
      const dt = dayjs(show.datetime);
      return dt.isAfter(latest) ? dt : latest;
    }, dayjs(initialShows[0].datetime));
    return latestShow.startOf("month");
  });

  // Build array of month keys from today to furthestMonth
  const allMonthKeys = useMemo(() => {
    const keys: string[] = [];
    let m = today;
    while (
      m.isBefore(furthestMonth, "month") ||
      m.isSame(furthestMonth, "month")
    ) {
      keys.push(m.format("YYYY-MM"));
      m = m.add(1, "month");
    }
    return keys;
  }, [today, furthestMonth]);

  // Query all months in the range
  const allMonthQueries = useQueries({
    queries: allMonthKeys.map((key) => ({
      queryKey: ["venueCalendarShows", venuePageId, key],
      queryFn: () =>
        fetchMonthShows(venuePageId, dayjs(key + "-01").startOf("month")),
      staleTime: 5 * 60 * 1000,
    })),
  });

  const allMonths: MonthData[] = useMemo(() => {
    const result: MonthData[] = [];
    for (let i = 0; i < allMonthKeys.length; i++) {
      const shows = allMonthQueries[i]?.data ?? [];
      if (shows.length > 0) {
        result.push({
          month: dayjs(allMonthKeys[i] + "-01").startOf("month"),
          shows,
        });
      }
    }
    return result;
  }, [allMonthKeys, allMonthQueries]);

  // Track consecutive empty months for infinite scroll end detection
  const [noMoreForwardScroll, setNoMoreForwardScroll] = useState(false);
  const scrollEmptyRef = useRef(0);

  const loadNextMonth = useCallback(() => {
    if (noMoreForwardScroll) return;
    setFurthestMonth((prev) => {
      const next = prev.add(1, "month");
      return next;
    });
  }, [noMoreForwardScroll]);

  // Detect when the furthest month query returns empty
  const furthestQuery = allMonthQueries[allMonthQueries.length - 1];
  const furthestKey = allMonthKeys[allMonthKeys.length - 1];
  const lastCheckedScrollRef = useRef<string>("");

  useEffect(() => {
    if (
      furthestKey &&
      furthestKey !== lastCheckedScrollRef.current &&
      furthestQuery &&
      !furthestQuery.isLoading
    ) {
      lastCheckedScrollRef.current = furthestKey;
      if ((furthestQuery.data ?? []).length === 0) {
        scrollEmptyRef.current += 1;
        if (scrollEmptyRef.current >= MAX_EMPTY_MONTHS) {
          setNoMoreForwardScroll(true);
        }
      } else {
        scrollEmptyRef.current = 0;
      }
    }
  }, [furthestKey, furthestQuery]);

  const isLoadingMore =
    allMonthQueries.length > 0 &&
    allMonthQueries[allMonthQueries.length - 1]?.isLoading === true;

  return {
    currentMonth,
    currentMonthShows,
    goToNextMonth,
    goToPrevMonth,
    canGoNext,
    canGoPrev,
    isLoading,
    allMonths,
    loadNextMonth,
    hasMore: !noMoreForwardScroll,
    isLoadingMore,
  };
}
